import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import {
  createLabeledPathWithI18n,
  type PathGroupKey as PathGroupKeyBase,
  pathGroupLabelI18nKeyMap,
  type PathKey as PathKeyBase,
  useMatchedPathKey,
} from '~/apps/ninesegsAnalyzer/routes/paths'
import { useBrandAccountAiEnabledQuery } from '~/hooks/useBrandAccountAiEnabledQuery'
import { useCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery'
import { useCurrentBrandAccountSurveyGroupsQueryData } from '~/hooks/useCurrentBrandAccountSurveyGroupsQuery'
import { useCurrentBrandAccountSurveysQueryData } from '~/hooks/useCurrentBrandAccountSurveysQuery'
import { useCurrentPrimarySurvey } from '~/hooks/useCurrentPrimarySurvey'
import { useIsMforceUser } from '~/hooks/useIsMforceUser'
import {
  BrandAccountOp,
  exists,
  type IconName,
  type NavigationGroup,
  type NavigationGroupItem,
  SurveyOp,
} from '~/utils'

const pathGroupKeys = [
  'documentManagement',
  'strategyMemoManagement',
  'timeSeriesReport',
  'brandAccountSettings',
  'basicAnalysis',
  'advancedAnalysis',
  'settings',
] as const satisfies PathGroupKeyBase[]
type PathGroupKey = (typeof pathGroupKeys)[number]

const iconMap: Record<PathGroupKey, IconName> = {
  advancedAnalysis: 'chart-bar-clustered',
  basicAnalysis: 'crosstab',
  brandAccountSettings: 'gears',
  documentManagement: 'document-manager',
  settings: 'gears',
  strategyMemoManagement: 'myspace',
  timeSeriesReport: 'chart-line-stacked',
}

const pathKeys = [
  'aiSettings',
  'basicOutputGeneration',
  'brandAccountAllowedDomainList',
  'brandAccountUserList',
  'brandGroupList',
  'customerAnalysis',
  'customerAttributeAnalysis',
  'customizedAnalysis',
  'dataSettings',
  'documentImageList',
  'documentQa',
  'documentReportList',
  'overlapAnalysis',
  'standardAnalysis',
  'whoAndWhatAnalysis',
  'strategyMemoList',
  'surveySettings',
  'timeSeriesAnalysis',
  'timeSeriesSettings',
] as const satisfies PathKeyBase[]
type PathKey = (typeof pathKeys)[number]

const pathMap: Record<PathGroupKey, PathKey[]> = {
  advancedAnalysis: [
    'customizedAnalysis',
    'overlapAnalysis',
    'customerAttributeAnalysis',
    'whoAndWhatAnalysis',
  ],
  basicAnalysis: [
    'basicOutputGeneration',
    'customerAnalysis',
    'standardAnalysis',
  ],
  brandAccountSettings: [
    'brandAccountUserList',
    'brandAccountAllowedDomainList',
    'aiSettings',
  ],
  documentManagement: ['documentQa', 'documentReportList', 'documentImageList'],
  settings: ['surveySettings', 'dataSettings', 'brandGroupList'],
  strategyMemoManagement: ['strategyMemoList'],
  timeSeriesReport: ['timeSeriesAnalysis', 'timeSeriesSettings'],
}

export const useNavigationGroups = () => {
  const { t } = useTranslation()
  const matchedPathKey = useMatchedPathKey(pathKeys)
  const brandAccount = useCommonQueryCurrentBrandAccount()
  const aiEnabledQuery = useBrandAccountAiEnabledQuery(brandAccount?.id)
  // Show this menu only once aiEnabled is confirmed true; it stays hidden while
  // loading or when disabled, so an AI-disabled account never sees it.
  const aiEnabled = aiEnabledQuery.data?.brandAccount?.aiEnabled === true
  const isMforceUser = useIsMforceUser(brandAccount?.id)
  const createLabeledPath = React.useMemo(
    () => createLabeledPathWithI18n(t),
    [t],
  )
  const brandAccountSurveys = useCurrentBrandAccountSurveysQueryData()
  const brandAccountSurveyGroups = useCurrentBrandAccountSurveyGroupsQueryData()
  const survey = useCurrentPrimarySurvey()

  const createNavigationGroupItem = React.useCallback(
    (pathKey: PathKey): NavigationGroupItem | false => {
      const labeledPath = match(pathKey)
        .with(
          'documentQa',
          (pathKey) =>
            exists(brandAccount) &&
            aiEnabled &&
            createLabeledPath(pathKey, { brandAccountId: brandAccount.id }),
        )
        .with(
          'documentReportList',
          'documentImageList',
          'strategyMemoList',
          (pathKey) =>
            exists(brandAccount) &&
            createLabeledPath(pathKey, { brandAccountId: brandAccount.id }),
        )
        .with(
          'timeSeriesAnalysis',
          (pathKey) =>
            exists(brandAccount) &&
            BrandAccountOp.hasReadiedSurveyGroups({
              ...brandAccount,
              surveyGroups: brandAccountSurveyGroups,
            }) &&
            createLabeledPath(pathKey, { brandAccountId: brandAccount.id }),
        )
        .with(
          'timeSeriesSettings',
          (pathKey) =>
            exists(brandAccount) &&
            brandAccountSurveys.some((survey) =>
              SurveyOp.hasMappedBrandGroupsWithBrands(survey),
            ) &&
            createLabeledPath(pathKey, { brandAccountId: brandAccount.id }),
        )
        .with(
          'basicOutputGeneration',
          'customerAnalysis',
          'standardAnalysis',
          'whoAndWhatAnalysis',
          'customizedAnalysis',
          'customerAttributeAnalysis',
          (pathKey) =>
            exists(survey) &&
            SurveyOp.hasMappedBrandGroupsWithBrands(survey) &&
            createLabeledPath(pathKey, { surveyId: survey.id }),
        )
        .with(
          'overlapAnalysis',
          (pathKey) =>
            exists(survey) &&
            SurveyOp.hasMappedBrandGroupsWithBrands(survey, 2) &&
            createLabeledPath(pathKey, { surveyId: survey.id }),
        )
        .with(
          'surveySettings',
          'dataSettings',
          'brandGroupList',
          (pathKey) =>
            exists(brandAccount) &&
            brandAccount.isSurveyEditor === true &&
            exists(survey) &&
            createLabeledPath(pathKey, { surveyId: survey.id }),
        )
        .with(
          'aiSettings',
          'brandAccountUserList',
          (pathKey) =>
            exists(brandAccount) &&
            createLabeledPath(pathKey, { brandAccountId: brandAccount.id }),
        )
        .with(
          'brandAccountAllowedDomainList',
          (pathKey) =>
            exists(brandAccount) &&
            isMforceUser &&
            createLabeledPath(pathKey, { brandAccountId: brandAccount.id }),
        )
        .exhaustive()

      return (
        labeledPath && { ...labeledPath, isActive: pathKey === matchedPathKey }
      )
    },
    [
      matchedPathKey,
      brandAccount,
      aiEnabled,
      createLabeledPath,
      brandAccountSurveyGroups,
      brandAccountSurveys,
      survey,
      isMforceUser,
    ],
  )

  return React.useMemo<NavigationGroup<PathGroupKey>[]>(
    () =>
      pathGroupKeys.map((pathGroupKey) => ({
        groupKey: pathGroupKey,
        icon: iconMap[pathGroupKey],
        isInitiallyExpanded: match(pathGroupKey)
          .with(
            'documentManagement',
            'strategyMemoManagement',
            'timeSeriesReport',
            'basicAnalysis',
            'advancedAnalysis',
            () => true,
          )
          .with('settings', 'brandAccountSettings', () =>
            R.isIncludedIn(matchedPathKey, pathMap.settings),
          )
          .exhaustive(),
        items: pathMap[pathGroupKey]
          .map(createNavigationGroupItem)
          .filter(R.isTruthy),
        label: t(pathGroupLabelI18nKeyMap[pathGroupKey]),
      })),
    [createNavigationGroupItem, matchedPathKey, t],
  )
}
